import {
  CustomEventTriggerStep,
  triggerShowsConditionSummary,
  TriggerStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { OmsTriggerKey } from "@redotech/redo-flows-common/oms-trigger-key";
import { RuleTriggerType } from "@redotech/redo-flows-common/rules/types";
import { SchemaType } from "@redotech/redo-flows-common/schemas/schemas";
import {
  CustomEventTriggerKey,
  getTriggerDisplayName,
  MarketingTriggerKey,
  omsAutomationListTriggers,
  providerFromSchemaType,
  rulesTriggers,
  rulesV3Triggers,
} from "@redotech/redo-flows-common/triggers";
import { useTranslation } from "@redotech/redo-i18n";
import { useDefaultRuleEditContext } from "@redotech/redo-merchant-support-app/setting/rules/default-rule-edit-context";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useMemo } from "react";
import { getTriggerIconForSchemaType } from "../nodes/trigger-node";
import {
  FrontendStepType,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "../steps";
import { expressionIsValid, triggerDataExpressionIsValid } from "./condition";
import { ConditionProviderContext } from "./condition-provider-context";
import { BackInStockFieldSelector } from "./trigger-steps/back-in-stock-trigger-step";
import { CustomEventTriggerInspector } from "./trigger-steps/custom-event-trigger-step";
import { EntryConditionsInspector } from "./trigger-steps/entry-conditions-inspector";
import { FrequencyCapInspector } from "./trigger-steps/frequency-cap-inspector";
import {
  DateFieldSelector,
  dateTriggerStepIsValid,
} from "./trigger-steps/marketing-date-trigger-step";
import { GiftCardBalanceFieldSelector } from "./trigger-steps/marketing-gift-card-balance-trigger-step";
import {
  PriceDropFieldSelector,
  priceDropTriggerStepIsValid,
} from "./trigger-steps/marketing-price-drop-trigger-step";
import { ShouldSkipSmartSendingInspector } from "./trigger-steps/should-skip-smart-sending-inspector";
import { SkipConditionsInspector } from "./trigger-steps/skip-conditions-inspector";
import { WorkOrderTriggerInspector } from "./work-order/work-order-trigger-inspector";

export const triggerStepType: FrontendStepType<
  PartialWithRequiredType<TriggerStep>
> = {
  valid: (state) => valid(state),
  selectable: (_) => true,
  type: "custom",
  customInspector: TriggerInspector,
};

function valid(state: PartialWithRequiredType<TriggerStep>) {
  if (!validateSkipConditions(state) || !validateEntryConditions(state)) {
    return false;
  }
  if (state.key === MarketingTriggerKey.DATE) {
    return dateTriggerStepIsValid(state);
  }

  if (state.key === MarketingTriggerKey.PRICE_DROP) {
    return priceDropTriggerStepIsValid(state);
  }
  return true;
}

function validateSkipConditions(state: PartialWithRequiredType<TriggerStep>) {
  if (!state.skipConditions) {
    return true;
  }
  if (
    !state.skipConditions.conditions ||
    !state.skipConditions.conjunctionMode
  ) {
    return false;
  }
  return state.skipConditions.conditions?.every((condition) =>
    expressionIsValid(condition),
  );
}

function validateEntryConditions(state: PartialWithRequiredType<TriggerStep>) {
  if (!state.entryConditions) {
    return true;
  }
  if (
    !state.entryConditions.conditions ||
    !state.entryConditions.conjunctionMode
  ) {
    return false;
  }
  return state.entryConditions.conditions.every((condition) =>
    triggerDataExpressionIsValid(condition?.schemaBooleanExpression),
  );
}

function TriggerInspector(
  props: StepTypeDetailsProps<PartialWithRequiredType<TriggerStep>>,
) {
  const key = props.step.key;
  const headerSection = <TriggerInspectorHeader {...props} />;
  const shouldSkipSmartSendingSection = (
    <ShouldSkipSmartSendingInspector {...props} />
  );
  const skipConditionsSection = <SkipConditionsInspector {...props} />;
  const entryConditionsSection = <EntryConditionsInspector {...props} />;
  const frequencyCapSection = <FrequencyCapInspector {...props} />;

  let triggerSpecificContent = null;
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (key) {
    case MarketingTriggerKey.DATE: {
      triggerSpecificContent = (
        <DateFieldSelector
          value={props.step}
          valueChange={(value) => {
            props.setStep(value);
          }}
        />
      );
      break;
    }
    case MarketingTriggerKey.PRICE_DROP: {
      triggerSpecificContent = (
        <PriceDropFieldSelector
          value={props.step}
          valueChange={(value) => {
            props.setStep(value);
          }}
        />
      );
      break;
    }
    case MarketingTriggerKey.GIFT_CARD_BALANCE: {
      triggerSpecificContent = (
        <GiftCardBalanceFieldSelector
          value={props.step}
          valueChange={(value) => {
            props.setStep(value);
          }}
        />
      );
      break;
    }
    case MarketingTriggerKey.BACK_IN_STOCK: {
      triggerSpecificContent = (
        <BackInStockFieldSelector
          value={props.step}
          valueChange={(value) => {
            props.setStep(value);
          }}
        />
      );
      break;
    }
    case CustomEventTriggerKey.CUSTOM_EVENT: {
      triggerSpecificContent = (
        <CustomEventTriggerInspector
          setStep={(updated) =>
            props.setStep(updated as PartialWithRequiredType<TriggerStep>)
          }
          step={props.step as PartialWithRequiredType<CustomEventTriggerStep>}
        />
      );
      break;
    }
  }
  if (props.step.schemaType === SchemaType.WORK_ORDER) {
    return (
      <Flex dir="column" gap="xl" pt="xl" w="full">
        {headerSection}
        <WorkOrderTriggerInspector
          automation={props.automation}
          setAutomation={props.setAutomation}
          setTriggerConditions={(value) => {
            props.setAutomation?.((prev) =>
              prev ? { ...prev, triggerConditions: value } : prev,
            );
          }}
          triggerConditions={props.triggerConditions}
        />
      </Flex>
    );
  }

  return (
    <ConditionProviderContext.Provider
      value={providerFromSchemaType(props.schemaType)}
    >
      <Flex dir="column" gap="3xl" pt="xl" w="full">
        {headerSection}
        {shouldSkipSmartSendingSection}
        {triggerSpecificContent}
        {triggerShowsConditionSummary(props.step) && (
          <>
            {entryConditionsSection}
            {frequencyCapSection}
            {skipConditionsSection}
          </>
        )}
      </Flex>
    </ConditionProviderContext.Provider>
  );
}

function TriggerInspectorHeader(
  props: StepTypeDetailsProps<PartialWithRequiredType<TriggerStep>>,
) {
  const { t } = useTranslation();
  const Icon = getTriggerIconForSchemaType(props.step.schemaType);

  // Default-rule templates fix the trigger; lock the type dropdown.
  const isDefaultRuleEdit = useDefaultRuleEditContext() !== null;

  const isV3 = props.step.schemaType === SchemaType.RULES_V3;
  const ruleTriggerOptions: RedoListItem<RuleTriggerType>[] = useMemo(() => {
    const triggers = isV3 ? rulesV3Triggers : rulesTriggers;
    return triggers.map((trigger) => ({
      id: trigger.key,
      type: "text" as const,
      text: trigger.displayName,
      value: trigger.key,
      description: trigger.description,
    }));
  }, [isV3]);

  const selectedItem = useMemo(() => {
    return ruleTriggerOptions.find((opt) => opt.value === props.step.key);
  }, [ruleTriggerOptions, props.step.key]);

  const omsTriggerOptions: RedoListItem<OmsTriggerKey>[] = useMemo(
    () =>
      omsAutomationListTriggers.map((trigger) => ({
        id: trigger.key,
        type: "text" as const,
        text: trigger.displayName,
        value: trigger.key,
        description: trigger.description,
      })),
    [],
  );

  const selectedOmsTrigger = useMemo(
    () => omsTriggerOptions.find((opt) => opt.value === props.step.key),
    [omsTriggerOptions, props.step.key],
  );

  return (
    <Flex dir="column" gap="md" w="full">
      <Flex align="center" justify="flex-start">
        <RedoFeaturedIcon
          color="black"
          Icon={Icon}
          size="sm"
          type="high-contrast"
        />
        <Text fontSize="sm" fontWeight="medium" textColor="primary">
          {props.step.schemaType === SchemaType.WORK_ORDER
            ? t("Work order flow")
            : (getTriggerDisplayName(props.step.key) ?? "")}
        </Text>
      </Flex>
      {props.step.category === "Rules" && (
        <RedoSingleSelectDropdownInput<RuleTriggerType>
          disabled={isDefaultRuleEdit}
          fitToAnchor
          label={t("Trigger type")}
          options={ruleTriggerOptions}
          optionSelected={({ item }) => {
            if (isDefaultRuleEdit) {
              return;
            }
            props.setStep({
              ...props.step,
              key: item.value,
              category: "Rules",
              schemaType: isV3 ? SchemaType.RULES_V3 : SchemaType.RULES,
            });
          }}
          placeholder={t("Select trigger type")}
          selectedItem={selectedItem}
        />
      )}
      {props.step.category === "Oms" && selectedOmsTrigger && (
        <RedoSingleSelectDropdownInput<OmsTriggerKey>
          fitToAnchor
          label={t("Trigger")}
          options={omsTriggerOptions}
          optionSelected={({ item }) => {
            const trigger = omsAutomationListTriggers.find(
              (t) => t.key === item.value,
            );
            if (!trigger) {
              return;
            }
            props.setStep({
              ...props.step,
              key: trigger.key,
              category: "Oms",
              schemaType: trigger.schemaType,
              triggerSpecificFields: undefined,
            });
          }}
          placeholder={t("Select a trigger")}
          readonly={!!props.automationId}
          selectedItem={selectedOmsTrigger}
        />
      )}
    </Flex>
  );
}
